import React, { useState } from 'react';
import { Topic } from '../../types/Topic';
import { SubjectType } from '../../types/Card';

interface ManualTopicEntryProps {
  onTopicAdded: (topic: Topic) => void;
  existingTopics?: Topic[];
}

const SUBJECT_OPTIONS: { value: SubjectType; label: string; color: string; emoji: string }[] = [
  { value: 'science', label: 'Science', color: '#4A90E2', emoji: '🔬' },
  { value: 'history', label: 'History', color: '#8B4513', emoji: '🏛️' },
  { value: 'geography', label: 'Geography', color: '#228B22', emoji: '🌍' },
  { value: 'maths', label: 'Mathematics', color: '#DC143C', emoji: '🔢' },
  { value: 'computing', label: 'Computing', color: '#FFD700', emoji: '💻' },
  { value: 'art', label: 'Art', color: '#FF8C00', emoji: '🎨' },
  { value: 'space', label: 'Space', color: '#9370DB', emoji: '🚀' }
];

export const ManualTopicEntry: React.FC<ManualTopicEntryProps> = ({ 
  onTopicAdded, 
  existingTopics = [] 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    subject: 'science' as SubjectType,
    yearGroup: 5,
    keyConcepts: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Validate topic name
    if (!formData.name.trim()) {
      newErrors.name = 'Topic name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Topic name must be at least 3 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Topic name must be less than 100 characters';
    } else if (existingTopics.some(topic => 
      topic.name.toLowerCase() === formData.name.trim().toLowerCase()
    )) {
      newErrors.name = 'This topic already exists';
    }
    
    // Validate year group
    if (formData.yearGroup < 1 || formData.yearGroup > 13) {
      newErrors.yearGroup = 'Year group must be between 1 and 13';
    }
    
    // Validate key concepts if provided
    if (formData.keyConcepts.trim()) {
      const concepts = formData.keyConcepts.split(',').map(c => c.trim()).filter(c => c.length > 0);
      if (concepts.length > 5) {
        newErrors.keyConcepts = 'Maximum 5 key concepts allowed';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const keyConcepts = formData.keyConcepts
      .split(',')
      .map(concept => concept.trim())
      .filter(concept => concept.length > 0)
      .slice(0, 5); // Limit to 5 concepts
    
    const newTopic: Topic = {
      name: formData.name.trim(),
      subject: formData.subject,
      yearGroup: formData.yearGroup,
      keyConcepts,
      confidence: 1.0 // Manual entry has full confidence
    };
    
    onTopicAdded(newTopic);
    
    // Reset form
    setFormData({
      name: '',
      subject: 'science',
      yearGroup: 5,
      keyConcepts: ''
    });
    setErrors({});
  };

  const handleInputChange = (field: keyof typeof formData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getSubjectInfo = (subject: SubjectType) => {
    return SUBJECT_OPTIONS.find(option => option.value === subject) || SUBJECT_OPTIONS[0];
  };

  const selectedSubjectInfo = getSubjectInfo(formData.subject);

  return (
    <div className="manual-topic-entry">
      <div className="entry-header">
        <h3>Add Topic Manually</h3>
        <p>Create custom curriculum topics for brain break card generation</p>
      </div>
      
      <form onSubmit={handleSubmit} className="topic-form">
        <div className="form-group">
          <label htmlFor="topic-name" className="form-label">
            Topic Name *
          </label>
          <input
            id="topic-name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="e.g., Forces and Motion, Ancient Egypt, Fractions"
            className={`form-input ${errors.name ? 'error' : ''}`}
            maxLength={100}
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="subject-select" className="form-label">
              Subject *
            </label>
            <div className="subject-selector">
              <span className="subject-preview" style={{ color: selectedSubjectInfo.color }}>
                {selectedSubjectInfo.emoji}
              </span>
              <select
                id="subject-select"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                className="form-select"
              >
                {SUBJECT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.emoji} {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="year-group" className="form-label">
              Year Group *
            </label>
            <input
              id="year-group"
              type="number"
              min="1"
              max="13"
              value={formData.yearGroup}
              onChange={(e) => handleInputChange('yearGroup', parseInt(e.target.value) || 1)}
              className={`form-input year-input ${errors.yearGroup ? 'error' : ''}`}
            />
            {errors.yearGroup && <span className="error-message">{errors.yearGroup}</span>}
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="key-concepts" className="form-label">
            Key Concepts <span className="optional">(optional)</span>
          </label>
          <input
            id="key-concepts"
            type="text"
            value={formData.keyConcepts}
            onChange={(e) => handleInputChange('keyConcepts', e.target.value)}
            placeholder="e.g., gravity, friction, acceleration (separate with commas)"
            className={`form-input ${errors.keyConcepts ? 'error' : ''}`}
            maxLength={200}
          />
          {errors.keyConcepts && <span className="error-message">{errors.keyConcepts}</span>}
          <span className="help-text">
            Enter up to 5 key concepts separated by commas
          </span>
        </div>
        
        <div className="form-actions">
          <button
            type="submit"
            disabled={!formData.name.trim()}
            className="submit-button"
          >
            <span className="button-icon">➕</span>
            Add Topic
          </button>
        </div>
      </form>
      
      {existingTopics.length > 0 && (
        <div className="existing-topics-info">
          <p className="info-text">
            {existingTopics.length} topic{existingTopics.length !== 1 ? 's' : ''} already added
          </p>
        </div>
      )}
    </div>
  );
};